/**
 * @file VariationReview.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-11-03
 * @modified 2026-11-03
 * @description Variation Review — opened from Pricing & Costing's header
 *              (Cam, 3 Nov 2026). It was Invoice Detail's Variation Summary
 *              window, in two stages; now its own window, in two steps:
 *
 *   1 Period — This month · Last month · Next month · Custom dates, filled in
 *     from Pricing & Costing's date range when it opens (changing it here does
 *     not change Pricing & Costing's).
 *   2 Review — every variation of the period (one customer and one product
 *     invoiced at more than one unit price ex VAT; till invoices only when
 *     booked to account), customers as sections in THE row order (account
 *     number, then name; product by code; invoice by date then number —
 *     utils/invoiceVariationReview.sortVariations, the server's order too).
 *     Each variation has ONE choice: Approve · Leave it (each with an optional
 *     reason, saved as chosen and as typed) · Edit the sale price · Flag it ·
 *     Make it a draft and flag it (each applied to its invoices one by one,
 *     after one quick question, the prior-month question on the way when the
 *     server asks). Under each invoice number: Book over… (BookOverInvoiceFlow)
 *     and Edit invoice… (the invoice editor in a window over this one —
 *     VariationEditInvoice), both returning here with everything as it was.
 *     A customer's header approves all its variations with one shared note;
 *     a customer whose every variation is resolved folds away with a tick.
 *     Everything stays changeable when the same period is run again: Approve
 *     ⇄ Leave ⇄ an action, the notes, "Put back" the price this review
 *     changed, clear a flag (the flagger or an admin), approve a draft again,
 *     book over again — the invoice edit's own rules (unpaid, the lock)
 *     deciding, a warning first when the invoice has gone out to the customer
 *     and the revised copy offered after (never sent by itself).
 *
 *   Server: /api/variation-review (routes/variationReview.js), flags cleared at
 *   /api/reports/invoice-variation-flags/:id/clear.
 *
 *   Props: { isOpen, onClose, initialFrom, initialTo, user }.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Modal from './Modal';
import DateInput from './DateInput';
import Input from './Input';
import Textarea from './Textarea';
import ConfirmDialog from './ConfirmDialog';
import AlertDialog from './AlertDialog';
import LoadingSpinner from './LoadingSpinner';
import BookOverInvoiceFlow from './BookOverInvoiceFlow';
import VariationEditInvoice from './VariationEditInvoice';
import { useSlideOutButton } from './SlideOutButton';
import { useEntityModal } from './EntityModalProvider';
import usePersistedState from '../hooks/usePersistedState';
import usePriorMonthConfirm from '../hooks/usePriorMonthConfirm';
import { API_URL } from '../config';
import { hasPermission } from '../utils/permissions';
import { formatCurrency, formatPrice } from '../utils/currencyFormatter';
import { formatQuantity } from '../utils/numberFormatters';
import { perUnitOf, priceFactsOf } from '../utils/unitPriceLabel';
import { lockMonthToday, lockMonthUkDate } from '../utils/xeroLockMonth';
import {
  NOTE_MAX, VARIATION_CHOICES, isInvoiceChoice, choiceLabel, periodBlock, periodPresets, presetFor, periodWords,
  resolutionOf, customersOf, summaryOf, doneWords, originalPrices, writeQuestion, actionProblem, cleanNote,
  clearFlagBlock, decisionWords
} from '../utils/invoiceVariationReview';
import './VariationReview.css';

const NOTE_SAVE_MS = 800;

/** "/T", "/25kg bag", " (unit not set)" — what a line's price is per. */
const perOf = (line) => perUnitOf(priceFactsOf({
  unit_weight_kg: line.unit_weight_kg,
  item_unit_weight_kg: line.item_unit_weight_kg,
  item_sales_account_code: line.item_sales_account_code || line.account_code
}));

const ukShort = (v) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v || ''));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
};

const getJson = async (res) => res.json().catch(() => ({}));

/**
 * Why a choice cannot be taken for this variation, or null. Approve / Leave
 * need only the right to act; an invoice action needs at least one invoice
 * that can take it. Pure.
 */
export function choiceBlock(variation, choice, actBlock) {
  if (actBlock) return actBlock;
  if (!isInvoiceChoice(choice)) return null;
  const invoices = variation.invoices || [];
  if (invoices.some(i => !i.actions?.[choice]?.block)) return null;
  const first = invoices.find(i => i.actions?.[choice]?.block);
  return first ? `${first.invoice_no}: ${first.actions[choice].block}` : 'No invoice can take it';
}

/**
 * Why "Book over…" is greyed for an invoice listed here, or null. A till
 * invoice booked to account may be booked over (moved to one date); one paid
 * at the till, or with no telling, must match the till — the server's till
 * rule (utils/tillInvoiceEdit.tillEditBlock) in its own words. Pure.
 */
export function bookOverWhy(inv) {
  if (!inv) return null;
  if (inv.paid) return 'Paid — only an unpaid invoice is booked over';
  if (inv.from_till && inv.till_payment !== 'account') {
    const then = 'correct the sale in Epos Now, or void it and invoice the sale again from Till Sales';
    if (inv.till_payment === 'paid') {
      const tenders = (Array.isArray(inv.till_tenders) ? inv.till_tenders : [])
        .filter(t => t && Math.abs(Number(t.amount) || 0) > 0.00001)
        .map(t => `${String(t.name || 'Unknown').trim()} £${Math.abs(Number(t.amount)).toFixed(2)}`);
      return `This invoice came from the till and was paid there${tenders.length ? ` (${tenders.join(', ')})` : ''}, so it must match the till — ${then}`;
    }
    return `This invoice came from the till, and how it was paid there cannot be told — ${then}`;
  }
  return null;
}

/** The step marker: numbered circles joined by a line — not buttons. */
function Steps({ step, periodText, onBack }) {
  const steps = [
    { n: 1, label: 'Period', detail: step > 1 ? periodText : 'Choose the dates' },
    { n: 2, label: 'Review', detail: 'Decide each variation' }
  ];
  return (
    <ol className="vr-steps" aria-label="Steps">
      {steps.map((s, i) => {
        const state = s.n < step ? 'is-done' : (s.n === step ? 'is-current' : 'is-todo');
        const body = (
          <>
            <span className="vr-step-dot" aria-hidden="true">
              {s.n < step ? <span className="material-symbols-rounded">check</span> : s.n}
            </span>
            <span className="vr-step-text">
              <span className="vr-step-label">{s.label}</span>
              <span className="vr-step-detail">{s.detail}</span>
            </span>
          </>
        );
        return (
          <React.Fragment key={s.n}>
            {i > 0 && <li className={`vr-step-line ${s.n <= step ? 'is-done' : ''}`} aria-hidden="true" />}
            <li className={`vr-step ${state}`} aria-current={s.n === step ? 'step' : undefined}>
              {s.n < step ? (
                <span className="vr-step-back" role="link" tabIndex={0} title="Go back to this step"
                  onClick={onBack} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onBack(); } }}>
                  {body}
                </span>
              ) : body}
            </li>
          </React.Fragment>
        );
      })}
    </ol>
  );
}

/** A status chip — solid semantic colour, dark text (the badge recipe). */
function StateChip({ state, text }) {
  const tone = { approved: 'success', acted: 'success', left: 'neutral', acting: 'warning', changed: 'error', to_do: 'outline' }[state] || 'outline';
  return <span className={`vr-chip vr-chip-${tone}`} title={text}>{text}</span>;
}

function VariationReviewBody({ initialFrom, initialTo, user, onClose }) {
  const today = lockMonthToday();
  const presets = useMemo(() => periodPresets(today), [today]);
  const [step, setStep] = useState(1);
  const [period, setPeriod] = useState(() => {
    const from = initialFrom || presets[1].from;
    const to = initialTo || presets[1].to;
    return { from, to, preset: presetFor(from, to, today) };
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  // Work not saved yet — survives closing the window and the editor (per tab).
  const [drafts, setDrafts] = usePersistedState('variationReview.drafts', {});
  const [openOverride, setOpenOverride] = useState({});
  const [filter, setFilter] = useState('todo');
  const [saving, setSaving] = useState({}); // key → 'saving' | 'saved' | 'error: …'
  const [ask, setAsk] = useState(null);     // { title, message, run }
  const [resendOffer, setResendOffer] = useState(null);
  const [alert, setAlert] = useState(null);
  const [bulk, setBulk] = useState(null);   // { accountId, note }
  const [bookingOverId, setBookingOverId] = useState(null);
  const [editing, setEditing] = useState(null); // invoice
  const priorMonth = usePriorMonthConfirm();
  const { openEntity } = useEntityModal();
  const noteTimers = useRef({});
  const wasDone = useRef({});
  const scrollRef = useRef(null);

  const periodKey = `${period.from}|${period.to}`;
  const draftOf = useCallback((key) => (drafts[periodKey] || {})[key] || {}, [drafts, periodKey]);
  const setDraft = (key, patch) => setDrafts(prev => {
    const p = { ...(prev[periodKey] || {}) };
    p[key] = { ...(p[key] || {}), ...patch };
    return { ...prev, [periodKey]: p };
  });
  const draftChoices = useMemo(() => {
    const out = {};
    for (const [k, d] of Object.entries(drafts[periodKey] || {})) if (d && d.choice) out[k] = { choice: d.choice };
    return out;
  }, [drafts, periodKey]);

  const whyPeriod = periodBlock(period.from, period.to);
  const query = `from=${encodeURIComponent(period.from)}&to=${encodeURIComponent(period.to)}`;

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const res = await fetch(`${API_URL}/api/variation-review?${query}`, { credentials: 'include' });
      const body = await getJson(res);
      if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
      setData(body);
    } catch (e) {
      setLoadError(e.message);
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => { if (step === 2) load(); }, [step, load]);

  const permBlock = !hasPermission(user, 'invoices.edit') ? "Deciding and acting on variations needs 'Edit invoices'" : null;
  const actBlock = data ? (data.can_act ? null : (data.act_block || 'You cannot change anything here')) : 'Loading…';
  const variations = useMemo(() => (data?.variations || []), [data]);
  const customers = useMemo(() => customersOf(variations, draftChoices), [variations, draftChoices]);
  const summary = useMemo(() => summaryOf(variations, draftChoices), [variations, draftChoices]);

  // A customer whose last variation was just resolved folds away by itself.
  useEffect(() => {
    const changed = {};
    for (const c of customers) {
      if (c.done && wasDone.current[c.account_id] === false) changed[c.account_id] = undefined;
      wasDone.current[c.account_id] = c.done;
    }
    if (Object.keys(changed).length) {
      setOpenOverride(prev => {
        const next = { ...prev };
        for (const id of Object.keys(changed)) delete next[id];
        return next;
      });
    }
  }, [customers]);

  useSlideOutButton([step, !!whyPeriod, loading, !!data]);

  // ---------------------------------------------------------------- saving
  const mergeDecisions = (decisions) => {
    if (!decisions) return;
    setData(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        variations: prev.variations.map(v => (decisions[v.key] ? { ...v, decision: { ...decisions[v.key], changed: false } } : v))
      };
    });
  };

  const decide = async (items, keys = items.map(i => i.key)) => {
    for (const k of keys) setSaving(s => ({ ...s, [k]: 'saving' }));
    try {
      const res = await fetch(`${API_URL}/api/variation-review/decide?${query}`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });
      const body = await getJson(res);
      if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
      mergeDecisions(body.decisions);
      const refused = new Map((body.refused || []).map(r => [r.key, r.reason]));
      for (const k of keys) setSaving(s => ({ ...s, [k]: refused.has(k) ? `error: ${refused.get(k)}` : 'saved' }));
      return true;
    } catch (e) {
      for (const k of keys) setSaving(s => ({ ...s, [k]: `error: ${e.message}` }));
      return false;
    }
  };

  const choose = (v, choice) => {
    const note = draftOf(v.key).note ?? v.decision?.note ?? '';
    setDraft(v.key, { choice, note });
    if (choice === 'approve' || choice === 'leave') {
      decide([{ key: v.key, approved: choice === 'approve', note: cleanNote(note) }]);
    }
  };

  const typeNote = (v, text) => {
    setDraft(v.key, { note: text });
    const choice = draftOf(v.key).choice || resolutionOf(v).choice;
    const decided = v.decision || choice === 'approve' || choice === 'leave';
    if (!decided) return; // an invoice action: it goes with the first one applied
    clearTimeout(noteTimers.current[v.key]);
    setSaving(s => ({ ...s, [v.key]: 'typing' }));
    noteTimers.current[v.key] = setTimeout(() => {
      const approved = v.decision ? (choice === 'approve' ? true : (choice === 'leave' || isInvoiceChoice(choice) ? false : v.decision.approved)) : choice === 'approve';
      decide([{ key: v.key, approved, note: cleanNote(text) }]);
    }, NOTE_SAVE_MS);
  };
  useEffect(() => () => Object.values(noteTimers.current).forEach(clearTimeout), []);

  /** Do invoice actions: [{ v, inv, action, prices?, note?, put_back? }]. */
  const perform = async (list) => {
    const keys = [...new Set(list.map(x => x.v.key))];
    for (const k of keys) setSaving(s => ({ ...s, [k]: 'saving' }));
    const actions = list.map(x => ({
      key: x.v.key,
      invoice_id: x.inv.invoice_id,
      action: x.action,
      note: cleanNote(x.note ?? draftOf(x.v.key).invoices?.[x.inv.invoice_id]?.note ?? '') || cleanNote(draftOf(x.v.key).note ?? x.v.decision?.note ?? ''),
      decision_note: cleanNote(draftOf(x.v.key).note ?? x.v.decision?.note ?? ''),
      ...(x.prices ? { prices: x.prices } : {}),
      ...(x.put_back ? { put_back: true } : {})
    }));
    try {
      const out = await priorMonth.run((extra = {}) => fetch(`${API_URL}/api/variation-review/perform?${query}`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ actions, ...extra })
      }));
      if (!out || out.declined) { for (const k of keys) setSaving(s => ({ ...s, [k]: null })); return; }
      if (!out.res.ok) throw new Error(out.data?.error || `HTTP ${out.res.status}`);
      const results = out.data.results || [];
      const refused = results.filter(r => !r.ok);
      for (const k of keys) {
        const bad = refused.find(r => r.key === k);
        setSaving(s => ({ ...s, [k]: bad ? `error: ${bad.invoice_no ? `${bad.invoice_no}: ` : ''}${bad.message}` : 'saved' }));
      }
      // Done: the typed prices and invoice notes go.
      setDrafts(prev => {
        const p = { ...(prev[periodKey] || {}) };
        for (const r of results.filter(x => x.ok)) {
          const d = { ...(p[r.key] || {}) };
          if (d.invoices) { d.invoices = { ...d.invoices }; delete d.invoices[r.invoice_id]; }
          p[r.key] = d;
        }
        return { ...prev, [periodKey]: p };
      });
      const resend = results.filter(r => r.ok && r.resend);
      if (resend.length) setResendOffer(resend);
      await load();
    } catch (e) {
      for (const k of keys) setSaving(s => ({ ...s, [k]: `error: ${e.message}` }));
    }
  };

  /** One quick question, then the action(s). */
  const askThen = (action, list) => {
    const q = writeQuestion(action, list.map(x => ({
      invoice_no: x.inv.invoice_no,
      warnings: (x.inv.actions?.[x.action]?.warnings || [])
    })));
    setAsk({ ...q, action, run: () => perform(list) });
  };

  const applyOne = (v, inv, action) => {
    const d = draftOf(v.key).invoices?.[inv.invoice_id] || {};
    if (action === 'leave') return perform([{ v, inv, action: 'leave', note: d.note || '' }]);
    const item = { v, inv, action, ...(action === 'edit_price' ? { prices: { ...(d.prices || {}) } } : {}) };
    return askThen(action, [item]);
  };

  const applyAll = (v, action) => {
    const list = v.invoices
      .filter(inv => (!inv.done || inv.done.action !== action) && !inv.actions?.[action]?.block)
      .map(inv => {
        const d = draftOf(v.key).invoices?.[inv.invoice_id] || {};
        return { v, inv, action, ...(action === 'edit_price' ? { prices: { ...(d.prices || {}) } } : {}) };
      });
    if (list.length) askThen(action, list);
  };

  const putBack = (v, inv) => {
    const prices = originalPrices(inv.edits);
    if (!prices) return;
    askThen('put_back', [{ v, inv, action: 'edit_price', put_back: true, prices: Object.fromEntries(Object.entries(prices).map(([k, p]) => [k, String(p)])), note: `Put back the price before this review changed it${cleanNote(draftOf(v.key).note) ? ` — ${cleanNote(draftOf(v.key).note)}` : ''}` }]);
  };

  const clearFlag = async (flag) => {
    try {
      const res = await fetch(`${API_URL}/api/reports/invoice-variation-flags/${flag.id}/clear`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({})
      });
      const body = await getJson(res);
      if (!res.ok || !body.success) throw new Error(body.error || `HTTP ${res.status}`);
      setAlert({ type: body.xeroWarning ? 'warning' : 'success', message: [body.message, body.xeroWarning].filter(Boolean).join('\n\n') });
      await load();
    } catch (e) {
      setAlert({ type: 'error', message: `The flag was not cleared: ${e.message}` });
    }
  };

  const sendRevised = async () => {
    const offers = resendOffer || [];
    setResendOffer(null);
    const sent = []; const notSent = [];
    for (const r of offers) {
      const to = r.resend?.to || [];
      if (!to.length) { notSent.push(`${r.invoice_no} (no address it went to — send it from Search Invoices)`); continue; }
      try {
        const res = await fetch(`${API_URL}/api/invoices/${r.invoice_id}/send-email`, {
          method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ to: to.join(', '), revised: true })
        });
        const body = await getJson(res);
        if (!res.ok || !body.success) throw new Error(body.error || 'not sent');
        sent.push(`${r.invoice_no} to ${to.join(', ')}`);
      } catch (e) {
        notSent.push(`${r.invoice_no} (${e.message})`);
      }
    }
    setAlert({
      type: notSent.length ? 'warning' : 'success',
      message: [sent.length ? `Revised copy sent: ${sent.join('; ')}.` : '', notSent.length ? `Not sent: ${notSent.join('; ')}.` : ''].filter(Boolean).join('\n\n')
    });
  };

  const approveCustomer = async (c, note) => {
    const items = c.variations.filter(v => !(v.decision && v.decision.approved && !v.decision.changed))
      .map(v => ({ key: v.key, approved: true, note: cleanNote(note) }));
    setBulk(null);
    if (!items.length) return;
    setDrafts(prev => {
      const p = { ...(prev[periodKey] || {}) };
      for (const i of items) p[i.key] = { ...(p[i.key] || {}), choice: 'approve', note: cleanNote(note) };
      return { ...prev, [periodKey]: p };
    });
    await decide(items);
  };

  // ---------------------------------------------------------------- render
  const choosePreset = (value) => {
    const p = presets.find(x => x.value === value);
    if (value === 'custom' || !p) setPeriod(prev => ({ ...prev, preset: 'custom' }));
    else setPeriod({ from: p.from, to: p.to, preset: value });
  };

  const shown = customers.filter(c => (filter === 'all' ? true : filter === 'todo' ? !c.done : c.done));

  const renderInvoiceAction = (v, inv, choice) => {
    const d = draftOf(v.key).invoices?.[inv.invoice_id] || {};
    const setInv = (patch) => setDraft(v.key, {
      invoices: { ...(draftOf(v.key).invoices || {}), [inv.invoice_id]: { ...d, ...patch } }
    });
    const check = inv.actions?.[choice] || {};
    const doneHere = inv.done && inv.done.action === choice;
    const parts = [];
    if (isInvoiceChoice(choice) && !doneHere) {
      if (check.block) {
        parts.push(<div key="b" className="vr-blocked"><span className="material-symbols-rounded">block</span>{check.block}</div>);
      } else {
        if (choice === 'edit_price') {
          parts.push(
            <div key="p" className="vr-price-inputs">
              {inv.lines.map(l => {
                const per = perOf(l);
                return (
                  <div key={l.line_id} className="vr-price-input">
                    <span className="vr-price-was">{formatPrice(l.unit_price)}{per.suffix}</span>
                    <span className="material-symbols-rounded vr-arrow" aria-hidden="true">arrow_forward</span>
                    <Input type="text" inputMode="decimal" placeholder="New price" value={d.prices?.[l.line_id] ?? ''}
                      aria-label={`New price for ${inv.invoice_no} line ${l.line_id}`} disabled={!!actBlock}
                      onChange={(e) => setInv({ prices: { ...(d.prices || {}), [l.line_id]: e.target.value } })} />
                    {v.usual && Math.abs(v.usual.price - l.unit_price) > 0.00005 && (
                      <button type="button" className="vr-link" disabled={!!actBlock}
                        onClick={() => setInv({ prices: { ...(d.prices || {}), [l.line_id]: String(v.usual.price) } })}
                        title={`${v.usual.lines} of the variation's ${v.usual.of} lines are at ${formatPrice(v.usual.price)}`}>
                        Use {formatPrice(v.usual.price)}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          );
        }
        const varNote = cleanNote(draftOf(v.key).note ?? v.decision?.note ?? '');
        const problem = actionProblem({ action: choice, note: d.note || varNote, prices: d.prices }, inv.lines);
        parts.push(
          <div key="a" className="vr-inv-apply">
            <Input type="text" placeholder={varNote ? `Note: uses the variation's — '${varNote}'` : 'Note (required)'} value={d.note || ''}
              aria-label={`Note for ${inv.invoice_no}`} maxLength={NOTE_MAX} disabled={!!actBlock}
              onChange={(e) => setInv({ note: e.target.value })} />
            <button type="button" className="btn-slideout always-out vr-apply" disabled={!!actBlock || !!problem}
              title={actBlock || problem || `${choiceLabel(choice)} on ${inv.invoice_no}`}
              data-testid={`apply-${inv.invoice_id}`}
              onClick={() => applyOne(v, inv, choice)}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">play_arrow</span></div>
              <span className="btn-label">Apply</span>
            </button>
          </div>
        );
        if (check.warnings && check.warnings.length) {
          parts.push(<ul key="w" className="vr-warnings">{check.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>);
        }
      }
      if (!inv.done) {
        parts.push(
          <button key="l" type="button" className="vr-link" disabled={!!actBlock} data-testid={`leave-${inv.invoice_id}`}
            onClick={() => applyOne(v, inv, 'leave')} title="Leave this invoice as it is — recorded on its history">
            Leave this one as it is
          </button>
        );
      }
    }
    if (inv.done) {
      parts.push(
        <div key="d" className="vr-done">
          <span className="material-symbols-rounded" aria-hidden="true">task_alt</span>
          <span>{doneWords(inv.done)}{inv.done.note ? ` — ‘${inv.done.note}’` : ''}</span>
        </div>
      );
    }
    // Reversing: the price this review changed, the flag it raised.
    const original = originalPrices(inv.edits);
    if (original && inv.edits.length) {
      const why = inv.actions?.edit_price?.block;
      parts.push(
        <button key="pb" type="button" className="vr-link" disabled={!!actBlock || !!why} data-testid={`put-back-${inv.invoice_id}`}
          title={actBlock || why || 'Put the price back to what it was before this review changed it'}
          onClick={() => putBack(v, inv)}>
          Put back {Object.values(original).map(p => formatPrice(p)).join(' / ')}
        </button>
      );
    }
    for (const f of inv.open_flags || []) {
      const why = clearFlagBlock(f, { userId: user?.id, isAdmin: user?.is_admin === true });
      parts.push(
        <div key={`f${f.id}`} className="vr-flag">
          <span className="vr-chip vr-chip-warning">Flagged{f.flagged_by_name ? ` by ${f.flagged_by_name}` : ''}</span>
          <button type="button" className="vr-link" disabled={!!why} title={why || ''} onClick={() => clearFlag(f)}>
            {f.made_draft ? 'Approve again & clear flag' : 'Clear flag'}
          </button>
        </div>
      );
    }
    return parts.length ? parts : <span className="vr-muted">No change to the invoice</span>;
  };

  const renderVariation = (v) => {
    const draft = draftOf(v.key);
    const res = resolutionOf(v, isInvoiceChoice(draft.choice) ? draft.choice : null);
    const choice = draft.choice || res.choice;
    const note = draft.note ?? v.decision?.note ?? '';
    const status = saving[v.key];
    return (
      <article key={v.key} className={`vr-variation ${res.resolved ? 'is-resolved' : ''}`} data-variation={v.key}>
        <header className="vr-var-head">
          <div className="vr-product">
            {v.item_code && <span className="vr-code">{v.item_code}</span>}
            <span className="vr-desc">{v.description || v.product_key}</span>
          </div>
          <div className="vr-prices">
            {v.prices.map(p => formatPrice(p)).join(' · ')}
            {v.usual ? <span className="vr-muted"> · usually {formatPrice(v.usual.price)} ({v.usual.lines} of {v.usual.of} lines)</span> : null}
            {v.agrees ? <span className="vr-muted"> · one price now</span> : null}
          </div>
          <StateChip state={res.state} text={res.text} />
        </header>

        <div className="vr-choice" role="radiogroup" aria-label={`What to do — ${v.label}`}>
          {VARIATION_CHOICES.map(c => {
            const why = choiceBlock(v, c.value, actBlock);
            return (
              <button key={c.value} type="button" role="radio" aria-checked={choice === c.value}
                className={`vr-seg ${choice === c.value ? 'is-on' : ''}`} disabled={!!why}
                title={why || c.label} data-choice={c.value} onClick={() => choose(v, c.value)}>
                <span className="material-symbols-rounded" aria-hidden="true">{c.icon}</span>
                {c.short}
              </button>
            );
          })}
        </div>

        <div className="vr-note-row">
          <Textarea rows={1} maxLength={NOTE_MAX} value={note} disabled={!!actBlock}
            aria-label={`Reason for ${v.label}`}
            placeholder={isInvoiceChoice(choice) ? 'Why (used as each invoice\'s note unless it has its own)' : 'Reason (optional) — kept on each of its invoices'}
            onChange={(e) => typeNote(v, e.target.value)} />
          <span className={`vr-save ${String(status || '').startsWith('error') ? 'is-error' : ''}`} role="status">
            {status === 'saving' ? 'Saving…' : status === 'typing' ? 'Not saved yet' : status === 'saved' ? 'Saved'
              : String(status || '').startsWith('error') ? `Not saved — ${String(status).slice(7)}`
                : (v.decision ? decisionWords(v.decision) : '')}
          </span>
        </div>

        {isInvoiceChoice(choice) && v.invoices.length > 1 && (
          <div className="vr-apply-all">
            <button type="button" className="vr-link" disabled={!!actBlock} onClick={() => applyAll(v, choice)}
              title="Every invoice that can take it and has what it needs — one question first">
              {choiceLabel(choice)} on every invoice that can take it
            </button>
          </div>
        )}

        <table className="vr-invoices">
          <thead>
            <tr><th>Invoice</th><th>Date</th><th>Status</th><th>Lines of this product</th><th className="text-right">Total</th><th>On this invoice</th></tr>
          </thead>
          <tbody>
            {v.invoices.map(inv => {
              const bookWhy = bookOverWhy(inv) || permBlock;
              const editWhy = inv.actions?.edit_invoice?.block || permBlock;
              return (
                <tr key={inv.invoice_id} data-invoice={inv.invoice_id}>
                  <td className="vr-inv-cell">
                    <span className="invoice-chip" role="button" tabIndex={0} title="Open the invoice"
                      onClick={() => openEntity('invoice', inv.invoice_id, { onClose: load })}
                      onKeyDown={(e) => { if (e.key === 'Enter') openEntity('invoice', inv.invoice_id, { onClose: load }); }}>
                      {inv.invoice_no}
                    </span>
                    <div className="vr-inv-links">
                      <button type="button" className="vr-link" data-write-action disabled={!!bookWhy}
                        title={bookWhy || 'Move it to another date, or split it over several invoices'}
                        onClick={() => setBookingOverId(inv.invoice_id)}>Book over…</button>
                      <button type="button" className="vr-link" data-testid={`edit-invoice-${inv.invoice_id}`} disabled={!!editWhy}
                        title={editWhy || `Open ${inv.invoice_no} in the invoice editor`}
                        onClick={() => setEditing(inv)}>Edit invoice…</button>
                    </div>
                  </td>
                  <td>{ukShort(inv.invoice_date)}</td>
                  <td>
                    <span className={`status-pill status-${String(inv.status || '').toLowerCase()}`}>{inv.status}</span>
                    <div className="vr-facts">
                      <span className="vr-fact">{inv.in_xero ? 'In Xero' : 'Not in Xero'}</span>
                      {inv.paid && <span className="vr-fact is-warning">Paid</span>}
                      {inv.gone_out && <span className="vr-fact is-warning" title={inv.gone_out}>Gone out</span>}
                      {inv.from_till && <span className="vr-fact" title="Booked to account at the till">Till · on account</span>}
                      {inv.order_number && <span className="vr-fact">{inv.order_number}</span>}
                    </div>
                  </td>
                  <td>
                    {inv.lines.map(l => {
                      const per = perOf(l);
                      return (
                        <div key={l.line_id} className="vr-line">
                          {formatQuantity(l.quantity)} at <strong>{formatPrice(l.unit_price)}</strong>
                          <span className={per.kind === 'unknown' ? 'vr-unit-unknown' : 'vr-muted'} title={per.title}>{per.suffix}</span>
                        </div>
                      );
                    })}
                  </td>
                  <td className="text-right">{formatCurrency(inv.total)}</td>
                  <td className="vr-action-td"><div className="vr-action-cell">{renderInvoiceAction(v, inv, choice)}</div></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </article>
    );
  };

  const periodText = periodWords(period.from, period.to);

  return (
    <div className="vr-root">
      <Steps step={step} periodText={periodText} onBack={() => setStep(1)} />

      {step === 1 ? (
        <section className="vr-period">
          <p className="vr-lead">
            Every customer and product invoiced at more than one unit price in the period. Filled in from Pricing &amp; Costing
            ({lockMonthUkDate(initialFrom) || '—'} – {lockMonthUkDate(initialTo) || '—'}) — change it here if you need to;
            Pricing &amp; Costing keeps its own dates.
          </p>
          <div className="vr-presets" role="radiogroup" aria-label="Period">
            {presets.map(p => (
              <label key={p.value} className={`vr-preset ${period.preset === p.value ? 'is-on' : ''}`}>
                <input type="radio" name="vr-period" value={p.value} checked={period.preset === p.value} onChange={() => choosePreset(p.value)} />
                <span className="vr-preset-label">{p.label}</span>
                <span className="vr-preset-detail">{p.month || 'Choose From and To'}</span>
              </label>
            ))}
          </div>
          <div className="vr-dates">
            <DateInput label="From" value={period.from} onChange={(e) => setPeriod(prev => ({ ...prev, from: e.target.value, preset: presetFor(e.target.value, prev.to, today) }))} />
            <DateInput label="To" value={period.to} onChange={(e) => setPeriod(prev => ({ ...prev, to: e.target.value, preset: presetFor(prev.from, e.target.value, today) }))} />
            <span className={`vr-period-words ${whyPeriod ? 'is-error' : ''}`}>{whyPeriod || periodText}</span>
          </div>
          <div className="vr-footer buttons slideout">
            <div className="vr-footer-gap" />
            <div>
              <button type="button" className="btn-slideout always-out vr-primary" disabled={!!whyPeriod}
                title={whyPeriod || `Review ${periodText}`} onClick={() => { setData(null); setStep(2); }}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">arrow_forward</span></div>
                <span className="btn-label">Review {periodText}</span>
              </button>
            </div>
          </div>
        </section>
      ) : (
        <section className="vr-review">
          <div className="vr-summary">
            <div className="vr-tile"><span className="vr-tile-n">{summary.variations}</span><span>variations · {summary.customers} customers</span></div>
            <div className="vr-tile is-success"><span className="vr-tile-n">{summary.resolved}</span><span>resolved</span></div>
            <div className="vr-tile is-warning"><span className="vr-tile-n">{summary.to_do}</span><span>to do</span></div>
            <div className="vr-tile-breakdown">
              {VARIATION_CHOICES.map(c => <span key={c.value}>{c.short} <strong>{summary[c.value]}</strong></span>)}
            </div>
          </div>
          <div className="vr-toolbar">
            <div className="vr-tabs" role="tablist">
              {[['todo', `To do (${customers.filter(c => !c.done).length})`], ['done', `Resolved (${customers.filter(c => c.done).length})`], ['all', `All (${customers.length})`]].map(([k, label]) => (
                <button key={k} type="button" role="tab" aria-selected={filter === k} className={`vr-tab ${filter === k ? 'is-on' : ''}`} onClick={() => setFilter(k)}>{label}</button>
              ))}
            </div>
            {data?.left_out?.text && <span className="vr-muted vr-left-out">{data.left_out.text}</span>}
          </div>
          {data && actBlock && <div className="vr-notice"><span className="material-symbols-rounded">info</span>{actBlock}</div>}

          <div className="vr-scroll" ref={scrollRef}>
            {loading && !data ? <LoadingSpinner /> : loadError && !data ? (
              <div className="vr-error">Failed to load: {loadError}</div>
            ) : !variations.length ? (
              <div className="vr-empty"><span className="material-symbols-rounded">check_circle</span>No customer was invoiced at more than one price for a product in {periodText}.</div>
            ) : !shown.length ? (
              <div className="vr-empty"><span className="material-symbols-rounded">task_alt</span>{filter === 'todo' ? 'Every variation is resolved.' : 'Nothing resolved yet.'}</div>
            ) : shown.map(c => {
              const open = openOverride[c.account_id] ?? !c.done;
              return (
                <section key={c.account_id} className={`vr-customer ${c.done ? 'is-done' : ''}`} data-customer={c.account_id}>
                  <header className="vr-cust-head">
                    <button type="button" className="vr-fold" aria-expanded={open}
                      aria-label={`${open ? 'Fold' : 'Open'} ${c.account_name}`}
                      onClick={() => setOpenOverride(prev => ({ ...prev, [c.account_id]: !open }))}>
                      <span className={`material-symbols-rounded ${open ? 'is-open' : ''}`}>chevron_right</span>
                    </button>
                    {c.account_no && <span className="account-badge-inline">{c.account_no}</span>}
                    <span className="vr-cust-name">{c.account_name}</span>
                    <span className="vr-muted">{c.resolved} of {c.total} resolved</span>
                    {c.done && <span className="material-symbols-rounded vr-tick" title="Every variation resolved" aria-label="Every variation resolved">check_circle</span>}
                    <span className="vr-cust-gap" />
                    {!c.done && c.total > 0 && (
                      <button type="button" className="btn-slideout always-out vr-approve-all" disabled={!!actBlock}
                        title={actBlock || `Approve every variation of ${c.account_name}, with one shared note`}
                        data-testid={`approve-all-${c.account_id}`}
                        onClick={() => setBulk({ accountId: c.account_id, note: '' })}>
                        <div className="icon-wrapper"><span className="material-symbols-rounded">done_all</span></div>
                        <span className="btn-label">Approve all {c.total}</span>
                      </button>
                    )}
                  </header>
                  {bulk && bulk.accountId === c.account_id && (
                    <div className="vr-bulk">
                      <Textarea rows={1} maxLength={NOTE_MAX} value={bulk.note} aria-label={`Shared note for ${c.account_name}`}
                        placeholder="One reason for them all (optional) — kept on each of their invoices"
                        onChange={(e) => setBulk({ ...bulk, note: e.target.value })} />
                      <button type="button" className="vr-link" onClick={() => setBulk(null)}>Cancel</button>
                      <button type="button" className="btn-slideout always-out vr-approve-all" data-testid={`approve-all-go-${c.account_id}`}
                        onClick={() => approveCustomer(c, bulk.note)}>
                        <div className="icon-wrapper"><span className="material-symbols-rounded">done_all</span></div>
                        <span className="btn-label">Approve {c.total}</span>
                      </button>
                    </div>
                  )}
                  {open && <div className="vr-cust-body">{c.variations.map(renderVariation)}</div>}
                </section>
              );
            })}
          </div>

          <div className="vr-footer buttons slideout">
            <div>
              <button type="button" className="btn-slideout" onClick={() => setStep(1)}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">arrow_back</span></div>
                <span className="btn-label">Change the period</span>
              </button>
            </div>
            <div>
              <button type="button" className="btn-slideout" onClick={load} disabled={loading}>
                <div className="icon-wrapper"><span className={`material-symbols-rounded ${loading ? 'spinning' : ''}`}>refresh</span></div>
                <span className="btn-label">Refresh</span>
              </button>
            </div>
            <div className="vr-footer-gap" />
            <div>
              <button type="button" className="btn-slideout" onClick={onClose}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
                <span className="btn-label">Close</span>
              </button>
            </div>
          </div>
        </section>
      )}

      <ConfirmDialog
        isOpen={!!ask}
        type="warning"
        title={ask?.title || ''}
        message={ask?.message || ''}
        confirmText="Go ahead"
        confirmIcon="play_arrow"
        cancelText="Not yet"
        onConfirm={() => { const run = ask.run; setAsk(null); run(); }}
        onCancel={() => setAsk(null)}
      />
      <ConfirmDialog
        isOpen={!!resendOffer}
        type="info"
        title="Send the customer a revised copy?"
        message={(resendOffer || []).map(r => `${r.resend?.gone_out || `${r.invoice_no} had gone out`}. ${(r.resend?.to || []).length
          ? `Send ${r.invoice_no}'s revised copy to ${r.resend.to.join(', ')}?` : `There is no address it went to — send ${r.invoice_no} from Search Invoices.`}`).join('\n\n')
          + '\n\nIt says "Revised" in the subject and on the PDF. Nothing is sent unless you say so.'}
        confirmText="Send revised copy"
        cancelText="Not now"
        onConfirm={sendRevised}
        onCancel={() => setResendOffer(null)}
      />
      <AlertDialog isOpen={!!alert} type={alert?.type || 'info'} message={alert?.message || ''} onClose={() => setAlert(null)} />
      {priorMonth.dialog}
      {bookingOverId && (
        <BookOverInvoiceFlow invoiceId={bookingOverId} onDone={() => load()} onClose={() => { setBookingOverId(null); load(); }} />
      )}
      {editing && (
        <VariationEditInvoice invoice={editing} onClose={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function VariationReview({ isOpen, onClose, initialFrom, initialTo, user }) {
  if (!isOpen) return null;
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Variation Review" size="xl" className="variation-review"
      closeOnOverlayClick={false} closeOnEscape={false}>
      <VariationReviewBody initialFrom={initialFrom} initialTo={initialTo} user={user} onClose={onClose} />
    </Modal>
  );
}

export default VariationReview;
